import React, { FunctionComponent } from 'react';
import { useTranslation } from 'react-i18next';
import { AxiosPromise } from 'axios';
import CommonGrid from 'shared/commonGrid';
import { SelectionChangedEvent, ColDef, ColGroupDef } from '@ag-grid-enterprise/all-modules';
import { ROWSELECTION } from 'shared/commonGrid/grid-enums';
import { CollectionFunctionGroup, CollectionFunctionGroupGridData } from 'data/collectionFunctionGroup/collectionFunctionGroup.types';
import { ServerResponse, UserSettings, GridSettings } from 'data/types';
import isTestEnvironment from 'utils/environment/isTestEnvironment';
import CollectionFunctionGroupListMenu from '../CollectionFunctionGroupListMenu';

type Props = {
  selectedCollectionFunctionGroup?: CollectionFunctionGroup;
  data: CollectionFunctionGroupGridData[];
  hasPrivilegesToCreate: boolean
  hasPrivilegeToDelete: boolean
  noResults: boolean,
  beforeLogout: boolean,
  lastUpdatedAt: Date | undefined,
  columnDefs: (ColGroupDef | ColDef)[],
  columnDefsVersion: string,
  autoGroupColumnDef: ColDef,
  suppressColumnVirtualisation?: boolean,
  suppressAggFuncInHeader: boolean,
  groupSelectsChildren: boolean,
  suppressRowClickSelection: boolean,
  enableGroupSelection: boolean,
  onRefresh: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
  onCreateNew: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
  onSelectionChanged: (event: SelectionChangedEvent) => void;
  onSaveGridSettings: (gridSettings: GridSettings) => void;
  onGetGridSettings: () => { type: string, payload: AxiosPromise<ServerResponse<UserSettings>> };
  onEdit: () => void;
  onDelete: () => void;
  onSelectItemMenuAction: () => void;
}

const CollectionFunctionGroupGrid: FunctionComponent<Props> = ({
  hasPrivilegesToCreate,
  hasPrivilegeToDelete,
  data = [],
  noResults,
  beforeLogout,
  lastUpdatedAt,
  columnDefs,
  columnDefsVersion,
  autoGroupColumnDef,
  suppressColumnVirtualisation = isTestEnvironment(),
  selectedCollectionFunctionGroup,
  suppressAggFuncInHeader,
  groupSelectsChildren,
  suppressRowClickSelection,
  enableGroupSelection,
  onRefresh,
  onCreateNew,
  onSelectionChanged,
  onSaveGridSettings,
  onGetGridSettings,
  onEdit,
  onDelete,
  onSelectItemMenuAction,
}: Props) => {
  const { t } = useTranslation();

  const handleEdit = () => {
    if (selectedCollectionFunctionGroup == null) {
      return;
    }

    onEdit();
  };

  return (
    <div className="grid-container grid-group-header-container" style={{ height: 'inherit' }}>
      <CommonGrid
        createNewText={t('collectionFunctionGroup.grid.createNew')}
        pagination
        enableBrowserTooltips
        rowSelection={ROWSELECTION.MULTIPLE}
        hasPrivilegeToCreateNew={hasPrivilegesToCreate}
        hasPrivilegeToDelete={hasPrivilegeToDelete}
        rowData={data}
        noResults={noResults}
        isBeforeLogout={beforeLogout}
        columnDefs={columnDefs}
        columnDefsVersion={columnDefsVersion}
        autoGroupColumnDef={autoGroupColumnDef}
        suppressColumnVirtualisation={suppressColumnVirtualisation}
        lastUpdatedAt={lastUpdatedAt}
        handleCreateNew={onCreateNew}
        onRefresh={onRefresh}
        onSelectionChanged={onSelectionChanged}
        saveGridSettings={onSaveGridSettings}
        getGridSettingsFromServer={onGetGridSettings}
        suppressAggFuncInHeader={suppressAggFuncInHeader}
        groupSelectsChildren={groupSelectsChildren}
        suppressRowClickSelection={suppressRowClickSelection}
        enableGroupSelection={enableGroupSelection}
        onActionMenuItemClick={onSelectItemMenuAction}
      >
        <CollectionFunctionGroupListMenu
          selectedCollectionFunctionGroup={selectedCollectionFunctionGroup}
          hasPrivilegeToCreateNew={hasPrivilegesToCreate}
          hasPrivilegeToDelete={hasPrivilegeToDelete}
          onEdit={handleEdit}
          onDelete={onDelete}
        />
      </CommonGrid>
    </div>
  );
};

export default CollectionFunctionGroupGrid;
